import { gql, useMutation, useQuery } from '@apollo/client'
import { useEffect, useState } from 'react'

import {
  CreateArtistThreadChannelMutation,
  CreateArtistThreadChannelMutationVariables,
  ThreadBoxFragmentDoc,
  ThreadCreateQuery,
  ThreadCreateQueryVariables,
  ThreadStatus,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'
import {
  getAcceptCommunityNote,
  setAcceptCommunityNote,
} from '@/core/native/storage'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { router } = useRouter<{ artistId: string }>()
  const [openCommunityNote, setOpenCommunityNote] = useState<boolean>(false)

  const artistId = router.query.artistId ?? config.artistId

  const { data } = useQuery<ThreadCreateQuery, ThreadCreateQueryVariables>(
    QUERY,
    { variables: { id: artistId } }
  )
  const [createArtistThreadChannel] = useMutation<
    CreateArtistThreadChannelMutation,
    CreateArtistThreadChannelMutationVariables
  >(CREATE_ARTIST_THREAD_CHANNEL)

  useEffect(() => {
    getAcceptCommunityNote().then(value => {
      setOpenCommunityNote(!value.value)
    })
  }, [])

  return {
    state: {
      artistId,
      artist: data?.artist ?? null,
      user: data?.user ?? null,
      openCommunityNote,
    },
    actions: {
      async onCreateThread({ thumbnail, ...inputs }) {
        await createArtistThreadChannel({
          variables: {
            inputs: {
              ...inputs,
              status: ThreadStatus.Published,
              artistId,
              thumbnail: thumbnail?.tmpPath
                ? { tmpPath: thumbnail?.tmpPath }
                : undefined,
            },
          },
        })
      },
      async onSaveDraft({ thumbnail, ...inputs }) {
        await createArtistThreadChannel({
          variables: {
            inputs: {
              ...inputs,
              status: ThreadStatus.Draft,
              artistId,
              thumbnail: thumbnail?.tmpPath
                ? { tmpPath: thumbnail?.tmpPath }
                : undefined,
            },
          },
        })
      },
      async onAcceptCommunityNote({ accept }) {
        if (accept) {
          await setAcceptCommunityNote('true')
        }
        setOpenCommunityNote(false)
      },
    },
  }
})

const QUERY = gql`
  query ThreadCreate($id: ID) {
    artist(id: $id) {
      patron {
        id
      }
      id
      displayName
      avatar {
        uri
      }
    }
    user {
      id
      avatar {
        uri
      }
      displayName
    }
  }
`

const CREATE_ARTIST_THREAD_CHANNEL = gql`
  mutation CreateArtistThreadChannel($inputs: CreateArtistThreadInput!) {
    createArtistThreadChannel(inputs: $inputs) {
      ...ThreadBox
    }
  }
  ${ThreadBoxFragmentDoc}
`
